import React from "react";
import {
  Badge,
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  SimpleGrid,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { BsChevronDown, BsChevronUp, BsPlus, BsThreeDotsVertical, BsUpload } from "react-icons/bs";
import templateApi from "../api/templateApi";
import ratingScaleApi from "../api/ratingScaleApi";
import ModulePageShell from "../components/common/ModulePageShell";
import OperationalPanel from "../components/common/OperationalPanel";
import PerformanceLocalNav from "../components/common/PerformanceLocalNav";
import StatusBadge from "../components/common/StatusBadge";
import { CompactEmptyState, InlineError, PageSkeleton } from "../components/common/PageState";
import PaginationControls from "../components/common/PaginationControls";
import performanceQueryKeys from "../utils/queryKeys";
import { formatDate, formatDateTime, personName, recordId, unwrapMeta, unwrapRows } from "../utils/normalize";

const getTemplateId = (template) => template?._id || template?.id;

const sourceLabels = {
  MANUAL: "Template items",
  CUSTOM: "Template items",
  EMPLOYEE_GOALS: "Employee goals",
  COMPETENCY_MASTER: "Competency master",
  "360_CAMPAIGN": "360 appraisal",
  STATIC_TEMPLATE_ITEMS: "Template items",
};

const sectionRules = (section = {}) => {
  const rules = [];
  if (section.isRequired || section.mandatory || section.required) rules.push("Required");
  if (section.allowEmployeeRating !== false) rules.push("Employee rating");
  if (section.allowManagerRating !== false) rules.push("Manager rating");
  if (section.commentRequired || section.commentsRequired) rules.push("Comments required");
  if (section.evidenceRequired) rules.push("Evidence required");
  return rules.length ? rules : ["No special rules"];
};

const itemTitle = (item = {}, index) =>
  item.title || item.name || item.label || item.kpiMeasure || item.itemCode || `Item ${index + 1}`;

const TemplateActionsMenu = ({ template, navigate, toast }) => {
  const templateId = getTemplateId(template);

  return (
    <Menu>
      <MenuButton
        as={IconButton}
        size="sm"
        variant="ghost"
        icon={<BsThreeDotsVertical />}
        aria-label="Template actions"
      />
      <MenuList>
        <MenuItem onClick={() => navigate(`/performance/pms-format/templates/${templateId}`)}>
          View details
        </MenuItem>
        <MenuItem onClick={() => navigate(`/performance/pms-format/templates/${templateId}/edit`)}>
          Edit
        </MenuItem>
        <MenuItem onClick={() => navigate(`/performance/pms-format/templates/${templateId}/versions`)}>
          Versions
        </MenuItem>
        <MenuItem
          onClick={() =>
            toast({
              title: "Archive uses the backend action menu.",
              description: "Open details before archiving a published template.",
              status: "info",
              duration: 2500,
            })
          }
        >
          Archive
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

const PerformanceTemplatesPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const panelBorder = useColorModeValue("gray.200", "gray.700");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const sectionBg = useColorModeValue("gray.50", "gray.900");
  const sectionCardBg = useColorModeValue("white", "gray.800");
  const [expandedTemplates, setExpandedTemplates] = React.useState(() => new Set());
  const [page, setPage] = React.useState(1);
  const [filters, setFilters] = React.useState({
    search: "",
    status: "",
    ratingScaleId: "",
    effectiveFrom: "",
    source: "",
  });

  const params = React.useMemo(
    () => ({
      page,
      limit: 20,
      search: filters.search || undefined,
      status: filters.status || undefined,
      ratingScaleId: filters.ratingScaleId || undefined,
      effectiveFrom: filters.effectiveFrom || undefined,
      source: filters.source || undefined,
    }),
    [filters, page],
  );

  const templatesQuery = useQuery({
    queryKey: performanceQueryKeys.templates.list(params),
    queryFn: () => templateApi.getPerformanceTemplates(params),
  });

  const ratingScalesQuery = useQuery({
    queryKey: performanceQueryKeys.ratingScales.list({ limit: 100 }),
    queryFn: () => ratingScaleApi.getPerformanceRatingScales({ limit: 100 }),
  });

  const rows = unwrapRows(templatesQuery.data);
  const meta = unwrapMeta(templatesQuery.data, rows.length);
  const ratingScales = unwrapRows(ratingScalesQuery.data);

  const updateFilter = (key, value) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const toggleSections = (templateId) => {
    setExpandedTemplates((current) => {
      const next = new Set(current);
      if (next.has(templateId)) {
        next.delete(templateId);
      } else {
        next.add(templateId);
      }
      return next;
    });
  };

  return (
    <ModulePageShell
      title="PMS format templates"
      description="Create and manage performance appraisal templates."
      onRefresh={() => templatesQuery.refetch()}
      actions={
        <Stack direction={{ base: "column", sm: "row" }} align={{ base: "stretch", sm: "center" }} spacing={2} width={{ base: "100%", md: "auto" }}>
          <Button
            size="sm"
            variant="outline"
            leftIcon={<BsUpload />}
            onClick={() => navigate("/performance/pms-format/templates/import")}
          >
            Import Excel
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            leftIcon={<BsPlus />}
            onClick={() => navigate("/performance/pms-format/templates/create")}
          >
            Create template
          </Button>
        </Stack>
      }
    >
      <PerformanceLocalNav />
      <VStack spacing={4} align="stretch">
        <OperationalPanel>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={3}>
            <Input
              size="sm"
              placeholder="Search templates"
              value={filters.search}
              onChange={(event) => updateFilter("search", event.target.value)}
            />
            <Select
              size="sm"
              placeholder="Status"
              value={filters.status}
              onChange={(event) => updateFilter("status", event.target.value)}
            >
              {["DRAFT", "PUBLISHED", "INACTIVE", "ARCHIVED", "INVALID"].map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </Select>
            <Select
              size="sm"
              placeholder="Rating scale"
              value={filters.ratingScaleId}
              onChange={(event) => updateFilter("ratingScaleId", event.target.value)}
            >
              {ratingScales.map((scale) => (
                <option key={recordId(scale)} value={recordId(scale)}>
                  {scale.name || scale.label || scale.code}
                </option>
              ))}
            </Select>
            <Input
              size="sm"
              type="date"
              value={filters.effectiveFrom}
              onChange={(event) => updateFilter("effectiveFrom", event.target.value)}
            />
            <HStack>
              <Select
                size="sm"
                placeholder="Source"
                value={filters.source}
                onChange={(event) => updateFilter("source", event.target.value)}
              >
                <option value="manual">Manual</option>
                <option value="excel">Excel</option>
                <option value="cloned">Cloned</option>
              </Select>
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  setFilters({
                    search: "",
                    status: "",
                    ratingScaleId: "",
                    effectiveFrom: "",
                    source: "",
                  })
                }
              >
                Reset
              </Button>
            </HStack>
          </SimpleGrid>
        </OperationalPanel>

        <OperationalPanel>
          {templatesQuery.isLoading ? (
            <PageSkeleton rows={6} />
          ) : templatesQuery.isError ? (
            <InlineError message="Unable to load appraisal templates." />
          ) : rows.length === 0 ? (
            <CompactEmptyState
              title="No appraisal templates found."
              message="Create a new template or import one from Excel."
            />
          ) : (
            <VStack align="stretch" spacing={4}>
              {rows.map((template) => {
                const templateId = getTemplateId(template);
                const sections = template.sections || [];
                const totalWeightage = template.totalWeightage ?? template.weightageTotal ?? 100;
                const isExpanded = expandedTemplates.has(templateId);

                return (
                  <Box key={templateId} borderWidth="1px" borderColor={panelBorder} borderRadius="md" overflow="hidden">
                    <Box p={4}>
                      <Flex justify="space-between" align={{ base: "stretch", lg: "flex-start" }} direction={{ base: "column", lg: "row" }} gap={3}>
                        <Box minW={0}>
                          <HStack wrap="wrap" spacing={2}>
                            <Text fontWeight="semibold" fontSize="md" wordBreak="break-word">
                              {template.name || template.title || "Untitled template"}
                            </Text>
                            <StatusBadge value={template.status || template.state || "DRAFT"} />
                            <Badge colorScheme={Number(totalWeightage) === 100 ? "green" : "orange"}>
                              {totalWeightage}% total
                            </Badge>
                          </HStack>
                          <Text color={mutedColor} fontSize="xs" wordBreak="break-word">
                            {template.code || "-"} · Version {template.version || template.currentVersion || "1"}
                          </Text>
                        </Box>
                        <HStack justify={{ base: "space-between", lg: "flex-end" }}>
                          <Button size="sm" variant="outline" onClick={() => navigate(`/performance/pms-format/templates/${templateId}`)}>
                            View details
                          </Button>
                          <TemplateActionsMenu template={template} navigate={navigate} toast={toast} />
                        </HStack>
                      </Flex>

                      <SimpleGrid columns={{ base: 1, sm: 2, lg: 5 }} spacing={3} mt={4}>
                        <Box>
                          <Text fontSize="xs" color={mutedColor}>Effective from</Text>
                          <Text fontSize="sm" fontWeight="medium">{formatDate(template.effectiveFrom || template.effectiveDate)}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color={mutedColor}>Rating scale</Text>
                          <Text fontSize="sm" fontWeight="medium">{template.ratingScaleId?.name || template.ratingScale?.name || "-"}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color={mutedColor}>Sections</Text>
                          <Text fontSize="sm" fontWeight="medium">{sections.length || template.sectionCount || 0}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color={mutedColor}>Created by</Text>
                          <Text fontSize="sm" fontWeight="medium" wordBreak="break-word">{personName(template.createdBy)}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color={mutedColor}>Updated</Text>
                          <Text fontSize="sm" fontWeight="medium">{formatDateTime(template.updatedAt)}</Text>
                        </Box>
                      </SimpleGrid>
                    </Box>

                    <Box borderTopWidth="1px" borderColor={panelBorder} bg={sectionBg}>
                      <Flex justify="space-between" align={{ base: "stretch", sm: "center" }} direction={{ base: "column", sm: "row" }} gap={3} p={4}>
                        <Box>
                          <Text fontWeight="semibold" fontSize="sm">Section data</Text>
                          <Text fontSize="xs" color={mutedColor}>
                            {sections.length || template.sectionCount || 0} sections configured
                          </Text>
                        </Box>
                        <Button
                          size="sm"
                          variant="outline"
                          rightIcon={isExpanded ? <BsChevronUp /> : <BsChevronDown />}
                          onClick={() => toggleSections(templateId)}
                        >
                          {isExpanded ? "Hide section data" : "Show section data"}
                        </Button>
                      </Flex>
                      <Collapse in={isExpanded} animateOpacity>
                        <Box px={4} pb={4}>
                          {sections.length === 0 ? (
                            <CompactEmptyState title="No sections configured." />
                          ) : (
                            <>
                              <VStack display={{ base: "flex", xl: "none" }} align="stretch" spacing={3}>
                                {sections.map((section, sectionIndex) => {
                                  const items = (section.items || []).filter((item) => item.active !== false);
                                  return (
                                    <Box key={section.sectionId || section.code || sectionIndex} bg={sectionCardBg} borderWidth="1px" borderColor={panelBorder} borderRadius="md" p={3}>
                                      <Flex justify="space-between" align="flex-start" gap={3}>
                                        <Box minW={0}>
                                          <Text fontSize="sm" fontWeight="semibold" wordBreak="break-word">
                                            Section {sectionIndex + 1}: {section.title || section.name || "Untitled section"}
                                          </Text>
                                          <Text fontSize="xs" color={mutedColor} wordBreak="break-word">{section.code || "-"}</Text>
                                        </Box>
                                        <Badge colorScheme="blue" flexShrink={0}>{section.weightage || 0}%</Badge>
                                      </Flex>
                                      <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3} mt={3}>
                                        <Box>
                                          <Text fontSize="xs" color={mutedColor}>Type</Text>
                                          <Text fontSize="sm">{section.type || "-"}</Text>
                                        </Box>
                                        <Box>
                                          <Text fontSize="xs" color={mutedColor}>Source</Text>
                                          <Text fontSize="sm">{sourceLabels[section.itemSource || section.sourceType] || section.itemSource || section.sourceType || "-"}</Text>
                                        </Box>
                                        <Box>
                                          <Text fontSize="xs" color={mutedColor}>Calculation</Text>
                                          <Text fontSize="sm">{section.calculationMethod || "-"}</Text>
                                        </Box>
                                        <Box>
                                          <Text fontSize="xs" color={mutedColor}>Items</Text>
                                          <Text fontSize="sm">{items.length}</Text>
                                        </Box>
                                      </SimpleGrid>
                                      <HStack mt={3} spacing={2} wrap="wrap">
                                        {sectionRules(section).map((rule) => (
                                          <Badge key={rule} variant={rule === "No special rules" ? "outline" : "subtle"}>{rule}</Badge>
                                        ))}
                                      </HStack>
                                      {items.length ? (
                                        <VStack mt={3} align="stretch" spacing={2}>
                                          {items.slice(0, 5).map((item, itemIndex) => (
                                            <Flex key={item.itemId || item.itemCode || itemIndex} justify="space-between" gap={3}>
                                              <Text fontSize="xs" wordBreak="break-word">{itemTitle(item, itemIndex)}</Text>
                                              <Text fontSize="xs" color={mutedColor} flexShrink={0}>{item.weightage || 0}%</Text>
                                            </Flex>
                                          ))}
                                          {items.length > 5 ? (
                                            <Text fontSize="xs" color={mutedColor}>+{items.length - 5} more items</Text>
                                          ) : null}
                                        </VStack>
                                      ) : null}
                                    </Box>
                                  );
                                })}
                              </VStack>

                              <TableContainer display={{ base: "none", xl: "block" }}>
                                <Table size="sm">
                                  <Thead>
                                    <Tr>
                                      <Th>Section</Th>
                                      <Th>Type</Th>
                                      <Th>Source</Th>
                                      <Th>Calculation</Th>
                                      <Th isNumeric>Weightage</Th>
                                      <Th isNumeric>Items</Th>
                                      <Th>Rules</Th>
                                      <Th>Item data</Th>
                                    </Tr>
                                  </Thead>
                                  <Tbody>
                                    {sections.map((section, sectionIndex) => {
                                      const items = (section.items || []).filter((item) => item.active !== false);
                                      return (
                                        <Tr key={section.sectionId || section.code || sectionIndex}>
                                          <Td>
                                            <Text fontWeight="semibold" fontSize="sm">{section.title || section.name || "Untitled section"}</Text>
                                            <Text color={mutedColor} fontSize="xs">{section.code || "-"}</Text>
                                          </Td>
                                          <Td>{section.type || "-"}</Td>
                                          <Td>{sourceLabels[section.itemSource || section.sourceType] || section.itemSource || section.sourceType || "-"}</Td>
                                          <Td>{section.calculationMethod || "-"}</Td>
                                          <Td isNumeric>{section.weightage || 0}%</Td>
                                          <Td isNumeric>{items.length}</Td>
                                          <Td>
                                            <HStack spacing={2} wrap="wrap">
                                              {sectionRules(section).map((rule) => (
                                                <Badge key={rule} variant={rule === "No special rules" ? "outline" : "subtle"}>{rule}</Badge>
                                              ))}
                                            </HStack>
                                          </Td>
                                          <Td maxW="360px">
                                            {items.length ? (
                                              <Text fontSize="xs" color={mutedColor} noOfLines={3}>
                                                {items.map((item, itemIndex) => `${itemTitle(item, itemIndex)} (${item.weightage || 0}%)`).join(", ")}
                                              </Text>
                                            ) : (
                                              <Text fontSize="xs" color={mutedColor}>No static items</Text>
                                            )}
                                          </Td>
                                        </Tr>
                                      );
                                    })}
                                  </Tbody>
                                </Table>
                              </TableContainer>
                            </>
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  </Box>
                );
              })}
              <PaginationControls page={meta.page} pages={meta.pages} onPageChange={setPage} />
            </VStack>
          )}
        </OperationalPanel>
      </VStack>
    </ModulePageShell>
  );
};

export default PerformanceTemplatesPage;
